/* eslint-disable import/no-extraneous-dependencies */
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';

import { H2, Text, NewButton, Icon, H3, Capitalize } from '../../../components';
import { useTermTranslations } from '../../chats/providers';
import { AddNewBusinessLogicForm } from '../components/organisms';

const BusinessLogicHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px 20px 0 20px;
`;

const BusinessLogicDescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 12px;
`;

const TableHeaderContainer = styled.div`
  padding: 20px 20px 0 20px;
`;

const TableContainer = styled.div`
  padding: 20px;
  --ag-font-size: 13px;
  --ag-font-family: var(--font-family-body);
  --ag-header-background-color: var(--wash-gray-default);
  --ag-header-foreground-color: var(--content-title);
  --ag-foreground-color: var(--content-body);
  --ag-background-color: var(--elevation-high);
  --ag-header-height: 40px;
  --ag-row-height: 40px;
  --ag-border-radius: 8px;
  --ag-row-hover-color: var(--elevation-mid);

  .ag-header-cell {
    font-weight: 600;
  }

  /* Remove minimum height constraint for autoHeight to work properly */
  .ag-center-cols-viewport {
    min-height: unset !important;
  }
`;

const capitalizeString = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const definitionCellRenderer = (params) => {
  const displayedDescription = params.data?.displayedDescription;
  const translation = params.data?.translation;

  const valueToDisplay = displayedDescription || translation || '';

  if (!valueToDisplay) return '';
  return capitalizeString(valueToDisplay);
};

const termListCellRenderer = (params) => {
  const termList = params.data?.termList;

  if (!termList || !Array.isArray(termList)) return '';

  return (
    <>
      {termList.map((term, index) => (
        <React.Fragment key={`${term}-${index}`}>
          <Text as='span' $bold>
            <Capitalize string={term} />
          </Text>
          {index < termList.length - 1 && <br />}
        </React.Fragment>
      ))}
    </>
  );
};

const BusinessLogic = () => {
  const { activeTermTranslations, pendingTermTranslations } = useTermTranslations();
  const [isFormOpen, setIsFormOpen] = useState(false);

  const activeBusinessLogicTermTranslations = useMemo(
    () => activeTermTranslations?.filter((tt) => tt?.type !== null) || [],
    [activeTermTranslations]
  );

  const columnDefs = [
    {
      headerName: 'Term',
      field: 'termList',
      minWidth: 100,
      resizable: false,
      cellRenderer: termListCellRenderer
    },
    {
      headerName: 'Definition',
      field: 'displayedDescription',
      flex: 2,
      minWidth: 300,
      resizable: false,
      cellRenderer: definitionCellRenderer
    }
  ];

  const handleFormSubmit = () => {
    // TODO: Add API call to save business logic
    setIsFormOpen(false);
  };

  const handleOpenForm = () => {
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
  };

  return (
    <>
      <BusinessLogicHeaderContainer>
        <BusinessLogicDescriptionContainer>
          <H2>Business Logic</H2>
          <Text $color='var(--content-subtitle)'>
            Define the rules and terminology your company uses, so we can apply them when they’re
            referenced in chat.
          </Text>
        </BusinessLogicDescriptionContainer>
        <NewButton primary onClick={handleOpenForm}>
          <Icon name='plus' />
          New Business Logic
        </NewButton>
      </BusinessLogicHeaderContainer>
      {pendingTermTranslations.length > 0 && (
        <>
          <TableHeaderContainer>
            <H3>Pending review</H3>
          </TableHeaderContainer>
          <TableContainer className='ag-theme-quartz'>
            <AgGridReact
              rowData={pendingTermTranslations}
              columnDefs={columnDefs}
              pagination={false}
              domLayout='autoHeight'
              suppressPaginationPanel={false}
              getRowId={(params) => params.data.id}
            />
          </TableContainer>
        </>
      )}
      {activeBusinessLogicTermTranslations.length > 0 && (
        <>
          <TableHeaderContainer>
            <H3>Active terms</H3>
          </TableHeaderContainer>
          <TableContainer className='ag-theme-quartz'>
            <AgGridReact
              rowData={activeBusinessLogicTermTranslations}
              columnDefs={columnDefs}
              pagination
              paginationPageSize={20}
              domLayout='autoHeight'
              suppressPaginationPanel={false}
              getRowId={(params) => params.data.id}
            />
          </TableContainer>
        </>
      )}

      <AddNewBusinessLogicForm
        isOpen={isFormOpen}
        onClose={handleCloseForm}
        onSubmit={handleFormSubmit}
      />
    </>
  );
};

export { BusinessLogic };
